import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Drawer, Modal, Spin } from 'antd';
import { useSession } from 'next-auth/react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import styled from 'styled-components';

import { ArrowLeftSvg, CrossSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import RotateSvg from '../../../assets/icons/RotateSvg';
import theme from '../../../assets/theme';
import useAutoFade from '../../../hooks/useAutoFade';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import useLiveGameImage from '../../../hooks/useLiveGameImage';
import { useMetrics } from '../../../hooks/useMetrics';
import { Response } from '../../../services/backendService/types';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { PreviewImage } from '../PreviewImage';

const ICON_SIZE = '25px';

const Controls = ({
  onRotateLeft,
  isModalVisible,
  handleSkipBackResponse,
  handleSkipForwardResponse,
}: {
  onRotateLeft: () => void;
  isModalVisible: boolean;
  handleSkipBackResponse: () => void;
  handleSkipForwardResponse: () => void;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { isInvisible } = useAutoFade({
    delay: 1000 * 2,
    eventListenerDependency: isModalVisible,
  });

  const session = useSession();
  const handleRPress = () => {
    onRotateLeft();
  };
  return (
    <ControlContainer $isInvisible={isInvisible}>
      <Hotkeys keyName="r" onKeyUp={handleRPress} />
      <ActionButton
        style={{ marginRight: '2px' }}
        onClick={() => {
          if (session.status === 'authenticated') {
            metrics.logEvent('ImagePreviewModal.SkipBack.Clicked');
          }
          handleSkipBackResponse();
        }}
      >
        <IconWrapper color="white" height="25px" width={ICON_SIZE}>
          <ArrowLeftSvg />
        </IconWrapper>
      </ActionButton>
      <IconButton onClick={onRotateLeft}>
        <RotateSvg />
        <span aria-label="press R to rotate" style={{ marginTop: 5, userSelect: 'none' }}>
          {t('Rotate (press R)')}
        </span>
      </IconButton>
      <ActionButton
        style={{ marginLeft: '2px' }}
        onClick={() => {
          if (session.status === 'authenticated') {
            metrics.logEvent('ImagePreviewModal.SkipForward.Clicked');
          }
          handleSkipForwardResponse();
        }}
      >
        <IconWrapper color="white" height="25px" width={ICON_SIZE}>
          <ArrowRightSvg />
        </IconWrapper>
      </ActionButton>
    </ControlContainer>
  );
};

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  transition-duration: 0.5s;
  padding: 10px;
  min-height: 30px;
  border-radius: 10px;
  margin-right: 5px;
  margin-left: 5px;
  color: white;
  cursor: pointer;
  outline: none;
  &:hover {
    background-color: #656570;
  }
  @media (max-width: 900px) {
    padding: 5px;
    margin: 0;
  }
`;

const IconButton = styled.div`
  background: transparent;
  padding: 8px 10px;
  border-radius: 5px;
  color: ${theme.colors.white};
  margin: 5px;
  svg {
    color: ${theme.colors.white};
    height: 20px;
    width: 20px;
    transform: scale(-1, 1);
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background-color: #656570;
  }
`;

const ControlContainer = styled.div<{ $isInvisible: boolean }>`
  opacity: ${({ $isInvisible }) => ($isInvisible ? '0' : '1')};
  transition: 0.5s;
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.colors.translucentLightBlack};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  color: white;
  margin-right: 20px;
  @media (max-width: 900px) {
    margin-right: 5px;
  }
`;

const ImagePreviewModal = ({
  type,
  visible,
  selectedResponse,
  selectedImageResponse,
  setSelectedResponse,
  setSelectedImageResponse,
  tenantId,
  handleSkipBackResponse,
  handleSkipForwardResponse,
}: {
  type?: 'image' | 'video';
  visible: boolean;
  selectedResponse: Response | undefined;
  selectedImageResponse: string | undefined;
  setSelectedResponse: Dispatch<SetStateAction<Response | undefined>>;
  setSelectedImageResponse: Dispatch<SetStateAction<string | undefined>>;
  tenantId: string;
  handleSkipBackResponse: () => void;
  handleSkipForwardResponse: () => void;
}) => {
  const isDesktop = useIsDesktop();

  const [rotation, setRotation] = useState(0);
  const [imageRotation, setImageRotation] = useState<
    { imageId: string; rotation: number }[]
  >([]);

  const { imageUrl, isLoading } = useLiveGameImage(
    tenantId,
    selectedImageResponse ? selectedResponse?.response : undefined,
    selectedImageResponse ? selectedResponse?.modifiedAt : undefined,
    true,
  );

  const getNextRotation = useCallback(
    (rotation: number) => {
      //if there is already a rotation for this image do not reset rotation
      if (selectedResponse) {
        const foundImage = imageRotation.find(
          image => image.imageId === selectedResponse.id,
        );

        if (foundImage?.rotation) {
          return foundImage?.rotation + 90;
        }
      }
      // Always rotate 90 degrees to the left because of the icon
      return rotation + 90;
    },
    [imageRotation, selectedResponse],
  );

  const handleImageRotation = () => {
    if (selectedResponse) {
      handleRotate();

      if (imageRotation.length === 0) {
        setImageRotation([
          { imageId: selectedResponse.id, rotation: getNextRotation(rotation) },
        ]);
      } else {
        const foundImageIndex = imageRotation.findIndex(
          image => image.imageId === selectedResponse.id,
        );
        if (foundImageIndex === -1) {
          setImageRotation([
            ...imageRotation,
            { imageId: selectedResponse.id, rotation: getNextRotation(rotation) },
          ]);
        } else {
          const newImageRotation = imageRotation.map((image, i) => {
            if (i === foundImageIndex) {
              return {
                imageId: selectedResponse.id,
                rotation: getNextRotation(rotation),
              };
            } else {
              return image;
            }
          });
          setImageRotation(newImageRotation);
        }
      }
    }
  };

  const handleRotate = useCallback(
    () => setRotation(rotation => getNextRotation(rotation)),
    [getNextRotation],
  );

  const findRotation = useMemo(() => {
    if (selectedResponse) {
      const foundImage = imageRotation.find(
        image => image.imageId === selectedResponse.id,
      );

      return foundImage ? foundImage.rotation : 0;
    }
    return 0;
  }, [selectedResponse, imageRotation]);

  useEffect(() => {
    if (!isLoading && imageUrl && imageUrl !== selectedImageResponse) {
      setSelectedImageResponse(imageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, selectedResponse]);

  useEffect(() => {
    setRotation(0);
  }, [selectedResponse]);

  const handleCancel = () => {
    setSelectedImageResponse(undefined);
    setSelectedResponse(undefined);
  };

  return (
    <>
      {isDesktop ? (
        <PreviewModal
          width="95%"
          visible={visible}
          onOk={handleCancel}
          onCancel={handleCancel}
          footer={null}
          closable={false}
          destroyOnClose
          bodyStyle={{ padding: 0 }}
          centered
        >
          <FullSizeDiv>
            <ModalRow>
              <CrossIconWrapper onClick={handleCancel}>
                <CrossSvg />
              </CrossIconWrapper>
            </ModalRow>
            <ImageContainer>
              {isLoading ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
              ) : (
                <>
                  {selectedImageResponse && (
                    <PreviewImage
                      type={type}
                      rotation={findRotation || 0}
                      src={selectedImageResponse}
                    />
                  )}
                </>
              )}
              {type !== 'video' && (
                <Controls
                  handleSkipBackResponse={handleSkipBackResponse}
                  handleSkipForwardResponse={handleSkipForwardResponse}
                  onRotateLeft={handleImageRotation}
                  isModalVisible={visible}
                />
              )}
            </ImageContainer>
          </FullSizeDiv>
        </PreviewModal>
      ) : (
        <BottomDrawer
          width="50%"
          style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
          placement="bottom"
          height="95%"
          closable={false}
          onClose={handleCancel}
          destroyOnClose
          visible={selectedImageResponse !== undefined}
          drawerStyle={{
            backgroundColor: '#ffffff',
          }}
        >
          <FullSizeDiv>
            <ModalRow>
              <CrossIconWrapper onClick={handleCancel}>
                <CrossSvg />
              </CrossIconWrapper>
            </ModalRow>
            <ImageContainer>
              {selectedImageResponse && (
                <PreviewImage
                  type={type}
                  rotation={findRotation}
                  src={selectedImageResponse}
                />
              )}
              <Controls
                onRotateLeft={handleImageRotation}
                isModalVisible={visible}
                handleSkipBackResponse={handleSkipBackResponse}
                handleSkipForwardResponse={handleSkipForwardResponse}
              />
            </ImageContainer>
          </FullSizeDiv>
        </BottomDrawer>
      )}
    </>
  );
};

export default ImagePreviewModal;

const PreviewModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px;
    box-shadow: none;
    height: 100vh;
    margin-top: 0;
    padding: 20px;
    background: transparent;
  }
  .ant-modal-body {
    box-shadow: none;
    height: 100%;
    width: 100%;
    background: white;
    border-radius: 20px;
  }
  .ant-modal-footer,
  .ant-modal-close {
    display: none;
  }
`;

const BottomDrawer = styled(Drawer)`
  .ant-drawer-content {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }
  .ant-drawer-body {
    padding: 0px;
    max-height: 100vh;
    overflow-y: auto;
  }
`;

const CrossIconWrapper = styled.div`
  color: ${theme.colors.black};
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 20px;
    width: 20px;
  }
  margin-right: 10px;
  cursor: pointer;
`;

const ModalRow = styled.div`
  padding: 0px 20px;
  display: flex;
  align-items: center;
  justify-content: end;
  width: 100%;
  height: 60px;
`;

const ImageContainer = styled.div`
  position: relative;
  height: calc(100% - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  overflow: hidden;
`;

const FullSizeDiv = styled.div`
  height: 100%;
  width: 100%;
`;
